import { useState, useEffect, useRef, useCallback } from 'react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RemediesDataManager, { type Remedy } from '../../utils/remediesData';
import { HomeopathyFactsManager, type HomeopathyFact } from '../../utils/homeopathyFacts';
import QuizAdminPanel from '../../components/feature/QuizAdminPanel';
import ImageUploadService from '../../services/imageUploadService';
import BadgeManager from '../../components/admin/BadgeManager';
import { sosGroups, sosDiseases as seedSosDiseases, type SOSOption } from '../../utils/sosData';
import { sosService, type SOSDisease } from '../../services/sosService';
import toast from '../../utils/toast';
import { IoDiamondOutline } from 'react-icons/io5';
import { FaPills } from 'react-icons/fa6';
import pushService from '../../services/pushService';

// Função auxiliar para obter o cabeçalho de autenticação
const getAuthHeader = () => {
  const tokensStr = localStorage.getItem('supabase.auth.token');
  if (!tokensStr) {
    console.error('Token de autenticação não encontrado no localStorage.');
    return null;
  }

  try {
    const tokens = JSON.parse(tokensStr);
    const accessToken = tokens.access_token;
    return {
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation'
    };
  } catch (e) {
    console.error('Erro ao parsear tokens do localStorage:', e);
    return null;
  }
};

const createEmptyGroups = (): SOSDisease['groups'] => {
  const groups: SOSDisease['groups'] = {};
  sosGroups.forEach((group) => {
    groups[group.id] = [];
  });
  return groups;
};

const getOptionIdentifier = (option: SOSOption) => option.id ?? option.label;

interface SortableGroupOptionProps {
  option: SOSOption;
  onRemove: () => void;
}

function SortableGroupOption({ option, onRemove }: SortableGroupOptionProps) {
  const identifier = getOptionIdentifier(option);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: identifier });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    opacity: isDragging ? 0.7 : 1,
    touchAction: 'none'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-red-100 text-red-700 px-3 py-2 rounded-xl text-sm flex items-center gap-2 shadow-sm"
    >
      <button
        type="button"
        className="text-red-500 cursor-grab flex items-center"
        {...attributes}
        {...listeners}
      >
        <i className="ri-drag-move-2-line text-lg"></i>
      </button>
      <span className="flex-1 leading-snug">{option.label}</span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        className="text-red-400 hover:text-red-600 transition-colors"
      >
        <i className="ri-close-line text-xs"></i>
      </button>
    </div>
  );
}

interface PointRule {
  id: string;
  name: string;
  description: string;
  points: number;
  category: 'positive' | 'negative';
  icon: string;
  active: boolean;
}

interface NewRemedy {
  name: string;
  scientificName: string;
  commonNames: string[];
  description: string;
  mainIndications: string[];
  potencies: string[];
  symptoms: string[];
  mentalSymptoms: string[];
  physicalSymptoms: string[];
  modalities: {
    worse: string[];
    better: string[];
  };
  constitution: string;
  category: 'mineral' | 'plant' | 'animal' | 'nosode' | 'sarcodio' | 'autotherapeutic' | 'isotherapeutic';
  image: string;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'treatment' | 'streak' | 'level' | 'quiz' | 'general';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  criteria: {
    type: string;
    count?: number;
    days?: number;
    level?: number;
  };
  points_reward: number;
  color: string;
  active: boolean;
}

export default function AdminDashboard() {
  const [testPushUserId, setTestPushUserId] = useState('');
  const [pushTitle, setPushTitle] = useState<string>('Teste de notificação');
  const [pushBody, setPushBody] = useState<string>('Se você recebeu, está tudo certo!');
  const [pushUsers, setPushUsers] = useState<{ user_id: string; count: number }[]>([]);
  const [loadingPushUsers, setLoadingPushUsers] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, signOut } = useAuth();
  const remediesManager = RemediesDataManager.getInstance();
  const factsManager = HomeopathyFactsManager.getInstance();
  const imageUploadService = ImageUploadService.getInstance();
  const lastDiseasesLoadKeyRef = useRef<string | null>(null);
  const lastDataLoadKeyRef = useRef<string | null>(null);

  // TODOS OS ESTADOS DEVEM VIR ANTES DE QUALQUER LÓGICA CONDICIONAL
  const [pointRules, setPointRules] = useState<PointRule[]>([
    {
      id: '1',
      name: 'Dose tomada no horário',
      description: 'Quando o usuário toma medicamento no horário correto',
      points: 10,
      category: 'positive',
      icon: 'ri-medicine-bottle-line',
      active: true
    },
    {
      id: '2',
      name: 'Dose tomada com atraso',
      description: 'Quando o usuário toma medicamento com atraso (até 2h)',
      points: 5,
      category: 'positive',
      icon: 'ri-time-line',
      active: true
    },
    {
      id: '3',
      name: 'Evento positivo registrado',
      description: 'Quando registra melhoria na saúde ou bem-estar',
      points: 15,
      category: 'positive',
      icon: 'ri-heart-fill',
      active: true
    },
    {
      id: '4',
      name: 'Dose perdida',
      description: 'Quando não toma medicamento programado',
      points: -5,
      category: 'negative',
      icon: 'ri-close-circle-line',
      active: true
    },
    {
      id: '5',
      name: 'Uso de medicamento alopático',
      description: 'Quando registra uso de medicamento não homeopático',
      points: -10,
      category: 'negative',
      icon: 'ri-medicine-bottle-fill',
      active: true
    },
    {
      id: '6',
      name: 'Registro de doença',
      description: 'Quando registra episódio de doença',
      points: -15,
      category: 'negative',
      icon: 'ri-virus-line',
      active: true
    }
  ]);

  // Usar o gerenciador centralizado para remédios
  const [remedies, setRemedies] = useState<Remedy[]>(remediesManager.getAllRemedies());

  // Estados para curiosidades
  const [facts, setFacts] = useState<HomeopathyFact[]>(factsManager.getAllFacts());

  // Estados para quiz
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [showQuizPanel, setShowQuizPanel] = useState(false);

  // Estados para badges
  const [badges, setBadges] = useState<Badge[]>([]);

  // Estados para SOS - Gerenciamento de Doenças
  const [diseases, setDiseases] = useState<SOSDisease[]>([]);
  const [showDiseaseModal, setShowDiseaseModal] = useState(false);
  const [showGroupOptionModal, setShowGroupOptionModal] = useState(false);
  const [editingDisease, setEditingDisease] = useState<SOSDisease | null>(null);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [newDisease, setNewDisease] = useState<Omit<SOSDisease, 'id'>>({
    name: '',
    description: '',
    active: true,
    groups: createEmptyGroups(),
    selectionLimits: {
      tipo_caracteristica: 1,
      piora_com: 1,
      melhora_com: 1,
      causas_sensacoes: 1,
      sintomas_concomitantes: 1,
      comportamentos_reacoes: 1,
      temporalidade_ritmo: 1
    }
  });
  const [newGroupOption, setNewGroupOption] = useState<Omit<SOSOption, 'id'>>({
    label: '',
    description: ''
  });
  const [loadingSosDiseases, setLoadingSosDiseases] = useState(false);
  const [savingDisease, setSavingDisease] = useState(false);
  const [seedingSosDiseases, setSeedingSosDiseases] = useState(false);
  const [diseaseError, setDiseaseError] = useState<string | null>(null);
  const [diseaseInfo, setDiseaseInfo] = useState<string | null>(null);

  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showEditRuleModal, setShowEditRuleModal] = useState(false);
  const [showRemedyModal, setShowRemedyModal] = useState(false);
  const [showFactModal, setShowFactModal] = useState(false);
  const [editingRule, setEditingRule] = useState<PointRule | null>(null);
  const [editingRemedy, setEditingRemedy] = useState<Remedy | null>(null);
  const [editingFact, setEditingFact] = useState<HomeopathyFact | null>(null);
  const [deletingRemedyId, setDeletingRemedyId] = useState<string | null>(null);
  const [newRule, setNewRule] = useState<Omit<PointRule, 'id'>>({
    name: '',
    description: '',
    points: 0,
    category: 'positive',
    icon: 'ri-star-line',
    active: true
  });

  const [newRemedy, setNewRemedy] = useState<NewRemedy>({
    name: '',
    scientificName: '',
    commonNames: [],
    description: '',
    mainIndications: [],
    potencies: [],
    symptoms: [],
    mentalSymptoms: [],
    physicalSymptoms: [],
    modalities: {
      worse: [],
      better: []
    },
    constitution: '',
    category: 'plant',
    image: ''
  });

  const [newFact, setNewFact] = useState<Omit<HomeopathyFact, 'id' | 'createdAt' | 'updatedAt'>>({
    title: '',
    content: '',
    category: 'general',
    difficulty: 'basic',
    tags: [],
    active: true
  });

    const [tempInput, setTempInput] = useState<Record<string, string>>({
    commonName: '',
    indication: '',
    potency: '',
    symptom: '',
    mentalSymptom: '',
    physicalSymptom: '',
    worse: '',
    better: '',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const rulesSectionRef = useRef<HTMLDivElement>(null);
  const remediesSectionRef = useRef<HTMLDivElement>(null);
  const factsSectionRef = useRef<HTMLDivElement>(null);
  const quizSectionRef = useRef<HTMLDivElement>(null);
  const sosSectionRef = useRef<HTMLDivElement>(null);
  const badgesSectionRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (sectionElement: HTMLElement | null) => {
    sectionElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Controle de acesso é feito pelo guard RequireAdmin na rota. Evitar redirecionamentos aqui para prevenir loops.

  useEffect(() => {
    const fetchDiseases = async () => {
      setLoadingSosDiseases(true);
      setDiseaseError(null);
      setDiseaseInfo(null);
      try {
        const data = await sosService.getDiseases();
        setDiseases(data);
        if (data.length === 0) {
          setDiseaseInfo('Nenhuma doença cadastrada no banco. Você pode importar os dados iniciais abaixo.');
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao carregar doenças SOS:', error);
        setDiseaseError('Não foi possível carregar as doenças SOS. Tente novamente.');
      } finally {
        setLoadingSosDiseases(false);
      }
    };

    const loadKey = user && isAdmin ? `${user.id}:${isAdmin}` : null;

    if (!loadKey) {
      lastDiseasesLoadKeyRef.current = null;
      return;
    }

    if (lastDiseasesLoadKeyRef.current === loadKey) {
      return;
    }

    lastDiseasesLoadKeyRef.current = loadKey;
    if (user && isAdmin) {
      fetchDiseases();
    }
  }, [user?.id, isAdmin, user]);

  // Carregar dados reais do banco de dados
  useEffect(() => {
    const loadDataFromDatabase = async () => {
      try {
        console.log('📊 [AdminDashboard] Carregando dados do banco...');
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        // Helper para fetch
        const fetchData = async (endpoint: string) => {
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${endpoint}`, { headers: authHeader });
          if (!response.ok) {
            throw new Error(`Erro ao buscar ${endpoint}: ${response.statusText}`);
          }
          return response.json();
        };

        // Carregar todos os dados em paralelo
        const [rulesData, remediesData, factsData, quizData, badgesData] = await Promise.all([
          fetchData('point_rules?select=*&order=created_at.asc'),
          fetchData('remedies?select=*&active=eq.true&order=name.asc'),
          fetchData('homeopathy_facts?select=*&order=created_at.desc'),
          fetchData('quiz_questions?select=*&order=created_at.desc'),
          fetchData('badges?select=*&order=created_at.desc'),
        ]);

        // Processar e setar regras de pontuação
        const formattedRules = rulesData.map((r: any) => ({ ...r, id: r.id.toString() }));
        setPointRules(formattedRules);
        console.log(`✅ [AdminDashboard] ${formattedRules.length} regras de pontuação carregadas`);

        // Processar e setar remédios
        const formattedRemedies = remediesData.map((r: any) => ({ ...r, id: r.id.toString() }));
        setRemedies(formattedRemedies);
        console.log(`✅ [AdminDashboard] ${formattedRemedies.length} remédios ativos carregados`);

        // Processar e setar curiosidades
        const formattedFacts = factsData.map((f: any) => ({ ...f, id: f.id.toString() }));
        setFacts(formattedFacts);
        console.log(`✅ [AdminDashboard] ${formattedFacts.length} curiosidades carregadas`);

        // Processar e setar badges
        const formattedBadges = badgesData.map((b: any) => ({ ...b, id: b.id.toString() }));
        setBadges(formattedBadges);
        console.log(`✅ [AdminDashboard] ${formattedBadges.length} badges carregados`);

        // Setar perguntas do quiz
        setQuizQuestions(quizData);
        console.log(`✅ [AdminDashboard] ${quizData.length} perguntas de quiz carregadas`);

        console.log('✅ [AdminDashboard] Todos os dados foram carregados com sucesso!');

      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao carregar dados:', error);
      }
    };

    const loadKey = user && isAdmin ? `${user.id}:${isAdmin}` : null;

    if (!loadKey) {
      lastDataLoadKeyRef.current = null;
      return;
    }

    if (lastDataLoadKeyRef.current === loadKey) {
      return;
    }

    lastDataLoadKeyRef.current = loadKey;
    if (user && isAdmin) {
      loadDataFromDatabase();
    }
  }, [user?.id, isAdmin, user]);

  // OBS: Não fazer early return antes de declarar TODOS os hooks
  // O redirecionamento é feito via useEffect acima; o retorno condicional
  // será feito logo antes do JSX, após todas as declarações de hooks.

  const getCategoryColor = (category: string) => {
    return category === 'positive'
      ? 'bg-emerald-100 text-emerald-600'
      : 'bg-red-100 text-red-600';
  };

  const getCategoryBorder = (category: string) => {
    return category === 'positive' ? 'border-emerald-200' : 'border-red-200';
  };

  const toggleRuleStatus = async (ruleId: string) => {
    try {
      const rule = pointRules.find(r => r.id === ruleId);
      if (!rule) return;

      const authHeader = getAuthHeader();
      if (!authHeader) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/point_rules?id=eq.${ruleId}`,
        {
          method: 'PATCH',
          headers: authHeader,
          body: JSON.stringify({ active: !rule.active })
        }
      );

      if (response.ok) {
        setPointRules(prev => prev.map(r => (r.id === ruleId ? { ...r, active: !r.active } : r)));
        console.log('✅ [AdminDashboard] Status da regra atualizado');
      } else {
        console.error('❌ [AdminDashboard] Falha ao atualizar status da regra:', await response.text());
      }
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao atualizar status:', error);
    }
  };

  const handleAddRule = async () => {
    if (newRule.name && newRule.description) {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/point_rules`,
          {
            method: 'POST',
            headers: authHeader,
            body: JSON.stringify({
              name: newRule.name,
              description: newRule.description,
              points: newRule.points,
              category: newRule.category,
              icon: newRule.icon,
              active: newRule.active,
              applies_to: 'member'
            })
          }
        );

        if (response.ok) {
          const [saved] = await response.json();
          setPointRules(prev => [...prev, saved]);
          console.log('✅ [AdminDashboard] Regra salva no banco');
          setNewRule({
            name: '',
            description: '',
            points: 0,
            category: 'positive',
            icon: 'ri-star-line',
            active: true
          });
          setShowAddRuleModal(false);
        } else {
          console.error('❌ [AdminDashboard] Falha ao adicionar regra:', await response.text());
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao salvar regra:', error);
      }
    }
  };

  const handleEditRule = async () => {
    if (editingRule && editingRule.name && editingRule.description) {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/point_rules?id=eq.${editingRule.id}`,
          {
            method: 'PATCH',
            headers: authHeader,
            body: JSON.stringify({
              name: editingRule.name,
              description: editingRule.description,
              points: editingRule.points,
              category: editingRule.category,
              icon: editingRule.icon,
              active: editingRule.active
            })
          }
        );

        if (response.ok) {
          const [updated] = await response.json();
          setPointRules(prev => prev.map(rule => (rule.id === editingRule.id ? updated : rule)));
          console.log('✅ [AdminDashboard] Regra atualizada no banco');
          setEditingRule(null);
          setShowEditRuleModal(false);
        } else {
          console.error('❌ [AdminDashboard] Falha ao editar regra:', await response.text());
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao atualizar regra:', error);
      }
    }
  };

  const handleAddRemedy = async () => {
    if (newRemedy.name && newRemedy.scientificName && newRemedy.description) {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/remedies`,
          {
            method: 'POST',
            headers: authHeader,
            body: JSON.stringify({
              name: newRemedy.name,
              scientific_name: newRemedy.scientificName,
              common_names: newRemedy.commonNames,
              description: newRemedy.description,
              main_indications: newRemedy.mainIndications,
              potencies: newRemedy.potencies,
              symptoms: newRemedy.symptoms,
              mental_symptoms: newRemedy.mentalSymptoms,
              physical_symptoms: newRemedy.physicalSymptoms,
              modalities_worse: newRemedy.modalities.worse,
              modalities_better: newRemedy.modalities.better,
              constitution: newRemedy.constitution,
              category: newRemedy.category,
              image_url: newRemedy.image,
              active: true
            })
          }
        );

        if (response.ok) {
          const [savedRemedy] = await response.json();
          setRemedies(prev => [...prev, savedRemedy]);
          console.log('✅ [AdminDashboard] Remédio salvo no banco:', savedRemedy.name);
          resetRemedyForm();
          setShowRemedyModal(false);
        } else {
          console.error('❌ [AdminDashboard] Falha ao adicionar remédio:', await response.text());
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao salvar remédio:', error);
      }
    }
  };

  const handleEditRemedy = async () => {
    if (editingRemedy && editingRemedy.name && editingRemedy.scientificName) {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/remedies?id=eq.${editingRemedy.id}`,
          {
            method: 'PATCH',
            headers: authHeader,
            body: JSON.stringify({
              name: editingRemedy.name,
              scientific_name: editingRemedy.scientificName,
              common_names: editingRemedy.commonNames,
              description: editingRemedy.description,
              main_indications: editingRemedy.mainIndications,
              potencies: editingRemedy.potencies,
              symptoms: editingRemedy.symptoms,
              mental_symptoms: editingRemedy.mentalSymptoms,
              physical_symptoms: editingRemedy.physicalSymptoms,
              modalities_worse: editingRemedy.modalities?.worse || [],
              modalities_better: editingRemedy.modalities?.better || [],
              constitution: editingRemedy.constitution,
              category: editingRemedy.category,
              image_url: editingRemedy.image
            })
          }
        );

        if (response.ok) {
          const [updated] = await response.json();
          setRemedies(prev => prev.map(r => (r.id === editingRemedy.id ? updated : r)));
          console.log('✅ [AdminDashboard] Remédio atualizado no banco');
          setEditingRemedy(null);
          setShowRemedyModal(false);
        } else {
          console.error('❌ [AdminDashboard] Falha ao editar remédio:', await response.text());
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao atualizar remédio:', error);
      }
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      const authHeader = getAuthHeader();
      if (!authHeader) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/point_rules?id=eq.${ruleId}`,
        {
          method: 'DELETE',
          headers: authHeader
        }
      );

      if (response.ok) {
        setPointRules(prev => prev.filter(rule => rule.id !== ruleId));
        console.log('✅ [AdminDashboard] Regra deletada do banco');
      } else {
        console.error('❌ [AdminDashboard] Falha ao deletar regra:', await response.text());
      }
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao deletar regra:', error);
    }
  };

  const deleteRemedy = async (remedyId: string) => {
    // Buscar o remédio para mostrar o nome na confirmação
    const remedy = remedies.find(r => r.id === remedyId);
    if (!remedy) {
      console.error('❌ [AdminDashboard] Remédio não encontrado no estado local:', remedyId);
      toast.error('Remédio não encontrado na lista atual. Tente recarregar a página.');
      return;
    }

    console.log('🔍 [AdminDashboard] Remédio encontrado:', remedy);

    // Confirmar antes de deletar
    const confirmed = window.confirm(
      `Tem certeza que deseja deletar o remédio "${remedy.name}"?\n\nEsta ação não pode ser desfeita e o remédio será removido permanentemente do banco de dados.`
    );

    if (!confirmed) {
      console.log('ℹ️ [AdminDashboard] Deletar remédio cancelado pelo usuário');
      return;
    }

    // Setar estado de loading
    setDeletingRemedyId(remedyId);
    console.log('🔄 [AdminDashboard] Iniciando operação de delete para:', remedy.name, '(ID:', remedyId, ')');

    try {
      console.log('🗑️ [AdminDashboard] Iniciando deletar remédio:', remedy.name, '(ID:', remedyId, ')');

      const authHeader = getAuthHeader();
      if (!authHeader) {
        console.error('❌ [AdminDashboard] Falha na autenticação - headers não disponíveis');
        toast.error('Erro de autenticação. Faça login novamente.');
        return;
      }

      console.log('🔐 [AdminDashboard] Headers de autenticação obtidos:', Object.keys(authHeader));

      const softDeleteUrl = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/remedies?id=eq.${remedyId}`;
      console.log('📡 [AdminDashboard] URL de soft delete (PATCH):', softDeleteUrl);

      const response = await fetch(softDeleteUrl, {
        method: 'PATCH',
        headers: authHeader,
        body: JSON.stringify({
          active: false,
          updated_at: new Date().toISOString()
        })
      });

      console.log('📡 [AdminDashboard] Resposta recebida - Status:', response.status, response.statusText);
      console.log('📡 [AdminDashboard] Headers de resposta:', Object.fromEntries(response.headers.entries()));

      const responseText = await response.text();
      console.log('📡 [AdminDashboard] Corpo da resposta (soft delete):', responseText || '<vazio>');

      let updatedRows: any[] = [];
      if (responseText) {
        try {
          updatedRows = JSON.parse(responseText);
        } catch (parseError) {
          console.warn('⚠️ [AdminDashboard] Não foi possível converter resposta em JSON:', parseError);
        }
      }

      if (response.ok && updatedRows.length > 0) {
        console.log('✅ [AdminDashboard] Remédio marcado como inativo no banco, processando limpeza local...');

        // Remover imagem do storage se existir
        if (remedy.image && remedy.image.includes('supabase.co/storage')) {
          try {
            console.log('🖼️ [AdminDashboard] Removendo imagem do storage:', remedy.image);
            await imageUploadService.deleteRemedyImage(remedy.image);
            console.log('✅ [AdminDashboard] Imagem removida do storage');
          } catch (error) {
            console.warn('⚠️ [AdminDashboard] Erro ao deletar imagem do storage:', error);
          }
        }

        // Verificar estado antes da atualização
        console.log('📊 [AdminDashboard] Estado antes da atualização:', remedies.length, 'remédios');

        // Atualizar estado local
        setRemedies(prev => {
          const filtered = prev.filter(r => r.id !== remedyId);
          console.log('📊 [AdminDashboard] Estado após filtro:', filtered.length, 'remédios');
          return filtered;
        });

        console.log('✅ [AdminDashboard] Remédio deletado com sucesso:', remedy.name);
        console.log('📊 [AdminDashboard] Verificando estado final...');

        // Verificar se o estado foi atualizado corretamente
        setTimeout(() => {
          console.log('📊 [AdminDashboard] Estado final após timeout:', remedies.length, 'remédios');
        }, 100);

        // Feedback visual
        toast.success(`Remédio "${remedy.name}" foi arquivado com sucesso!`);

      } else {
        console.error('❌ [AdminDashboard] Falha ao arquivar remédio - Status:', response.status);
        console.error('❌ [AdminDashboard] Headers de resposta:', Object.fromEntries(response.headers.entries()));
        console.error('❌ [AdminDashboard] Corpo da resposta:', responseText);

        // Fallback: tentar RPC com SECURITY DEFINER para contornar RLS
        try {
          console.log('🛠️ [AdminDashboard] Tentando fallback via RPC archive_remedy...');
          const rpcUrl = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/archive_remedy`;
          const rpcResp = await fetch(rpcUrl, {
            method: 'POST',
            headers: authHeader,
            body: JSON.stringify({ remedy_id: remedyId })
          });

          const rpcText = await rpcResp.text();
          console.log('📡 [AdminDashboard] RPC Status:', rpcResp.status, rpcResp.statusText);
          console.log('📡 [AdminDashboard] RPC Body:', rpcText || '<vazio>');

          let rpcRows: any[] = [];
          if (rpcText) {
            try { rpcRows = JSON.parse(rpcText); } catch { /* mantém vazio */ }
          }

          if (rpcResp.ok && rpcRows.length > 0) {
            console.log('✅ [AdminDashboard] RPC aplicou o arquivamento, limpando item local...');
            if (remedy.image && remedy.image.includes('supabase.co/storage')) {
              try {
                await imageUploadService.deleteRemedyImage(remedy.image);
                console.log('✅ [AdminDashboard] Imagem removida do storage (via RPC)');
              } catch (error) {
                console.warn('⚠️ [AdminDashboard] Erro ao deletar imagem do storage (via RPC):', error);
              }
            }
            setRemedies(prev => prev.filter(r => r.id !== remedyId));
            toast.success(`Remédio "${remedy.name}" foi arquivado com sucesso!`);
          } else {
            // RPC também falhou -> manter mensagens específicas
            if (response.status === 401) {
              toast.error('Erro de autenticação. Faça login novamente.');
            } else if (response.status === 403) {
              toast.error('Você não tem permissão para arquivar este remédio. Verifique as políticas RLS.');
            } else if (response.status === 404) {
              toast.warning('Remédio não encontrado. Pode já ter sido arquivado.');
            } else if (response.status === 405) {
              toast.warning('Método não permitido para esta operação.');
            } else if (response.status === 422) {
              toast.error('Dados inválidos na requisição.');
            } else {
              toast.error(`Erro ao arquivar remédio: ${response.status} - ${response.statusText}. ${responseText || ''}`);
            }
          }
        } catch (rpcError) {
          console.error('❌ [AdminDashboard] Erro no fallback RPC:', rpcError);
          toast.error('Erro ao tentar arquivar via RPC. Tente novamente.');
        }
      }
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro de rede ao deletar remédio:', error);
      toast.error('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      // Limpar estado de loading
      setDeletingRemedyId(null);
      console.log('🔄 [AdminDashboard] Operação de delete finalizada');
    }
  };

  // Funções para gerenciar curiosidades
  const handleAddFact = async () => {
    if (newFact.title && newFact.content) {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/homeopathy_facts`,
          {
            method: 'POST',
            headers: authHeader,
            body: JSON.stringify(newFact)
          }
        );

        if (response.ok) {
          const [savedFact] = await response.json();
          setFacts(prev => [savedFact, ...prev]);
          console.log('✅ [AdminDashboard] Curiosidade salva no banco');
          resetFactForm();
          setShowFactModal(false);
        } else {
          console.error('❌ [AdminDashboard] Falha ao adicionar curiosidade:', await response.text());
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao salvar curiosidade:', error);
      }
    }
  };

  const handleEditFact = async () => {
    if (editingFact && editingFact.title && editingFact.content) {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/homeopathy_facts?id=eq.${editingFact.id}`,
          {
            method: 'PATCH',
            headers: authHeader,
            body: JSON.stringify({
              title: editingFact.title,
              content: editingFact.content,
              category: editingFact.category,
              difficulty: editingFact.difficulty,
              tags: editingFact.tags,
              active: editingFact.active
            })
          }
        );

        if (response.ok) {
          const [updated] = await response.json();
          setFacts(prev => prev.map(f => (f.id === editingFact.id ? updated : f)));
          console.log('✅ [AdminDashboard] Curiosidade atualizada no banco');
          setEditingFact(null);
          setShowFactModal(false);
        } else {
          console.error('❌ [AdminDashboard] Falha ao editar curiosidade:', await response.text());
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Erro ao atualizar curiosidade:', error);
      }
    }
  };

  const deleteFact = async (factId: string) => {
    try {
      const authHeader = getAuthHeader();
      if (!authHeader) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/homeopathy_facts?id=eq.${factId}`,
        {
          method: 'DELETE',
          headers: authHeader
        }
      );

      if (response.ok) {
        setFacts(prev => prev.filter(f => f.id !== factId));
        console.log('✅ [AdminDashboard] Curiosidade deletada do banco');
      } else {
        console.error('❌ [AdminDashboard] Falha ao deletar curiosidade:', await response.text());
      }
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao deletar curiosidade:', error);
    }
  };

  const toggleFactStatus = async (factId: string) => {
    try {
      const fact = facts.find(f => f.id === factId);
      if (!fact) return;

      const authHeader = getAuthHeader();
      if (!authHeader) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/homeopathy_facts?id=eq.${factId}`,
        {
          method: 'PATCH',
          headers: authHeader,
          body: JSON.stringify({ active: !fact.active })
        }
      );

      if (response.ok) {
        setFacts(prev => prev.map(f => (f.id === factId ? { ...f, active: !f.active } : f)));
        console.log('✅ [AdminDashboard] Status da curiosidade atualizado');
      } else {
        console.error('❌ [AdminDashboard] Falha ao atualizar status da curiosidade:', await response.text());
      }
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao atualizar status da curiosidade:', error);
    }
  };

  const resetFactForm = () => {
    setNewFact({
      title: '',
      content: '',
      category: 'general',
      difficulty: 'basic',
      tags: [],
      active: true
    });
  };

  const resetRemedyForm = () => {
    setNewRemedy({
      name: '',
      scientificName: '',
      commonNames: [],
      description: '',
      mainIndications: [],
      potencies: [],
      symptoms: [],
      mentalSymptoms: [],
      physicalSymptoms: [],
      modalities: {
        worse: [],
        better: []
      },
      constitution: '',
      category: 'plant',
      image: ''
    });
    setTempInput({
      commonName: '',
      indication: '',
      potency: '',
      symptom: '',
      mentalSymptom: '',
      physicalSymptom: '',
      worse: '',
      better: ''
    });
  };

  // Funções auxiliares para o modal de doença
  const addOptionToGroup = (groupId: string) => {
    const inputValue = tempInput[`group_${groupId}`];
    if (!inputValue?.trim()) return;

    const newOption: SOSOption = {
      id: `option_${Date.now()}`,
      label: inputValue.trim(),
      description: ''
    };

    if (editingDisease) {
      setEditingDisease({
        ...editingDisease,
        groups: {
          ...editingDisease.groups,
          [groupId]: [...(editingDisease.groups[groupId as keyof typeof editingDisease.groups] || []), newOption]
        }
      });
    } else {
      setNewDisease((prev) => ({
        ...prev,
        groups: {
          ...prev.groups,
          [groupId]: [...(prev.groups[groupId] || []), newOption],
        },
      }));
    }

    setTempInput((prev) => ({ ...prev, [`group_${groupId}`]: '' }));
  };

  const removeOptionFromGroup = (groupId: string, optionIndex: number) => {
    if (editingDisease) {
      const currentOptions = editingDisease.groups[groupId as keyof typeof editingDisease.groups] || [];
      const updatedOptions = currentOptions.filter((_, index) => index !== optionIndex);
      setEditingDisease({
        ...editingDisease,
        groups: {
          ...editingDisease.groups,
          [groupId]: updatedOptions
        }
      });
    } else {
      setNewDisease((prev) => ({
        ...prev,
        groups: {
          ...prev.groups,
          [groupId]: (prev.groups[groupId] || []).filter((_, index) => index !== optionIndex),
        },
      }));
    }
  };

  const handleGroupOptionReorder = (groupId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const options = editingDisease
      ? editingDisease.groups[groupId as keyof typeof editingDisease.groups] || []
      : newDisease.groups[groupId] || [];

    const oldIndex = options.findIndex(option => getOptionIdentifier(option) === active.id);
    const newIndex = options.findIndex(option => getOptionIdentifier(option) === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reordered = arrayMove(options, oldIndex, newIndex);

    if (editingDisease) {
      setEditingDisease({
        ...editingDisease,
        groups: {
          ...editingDisease.groups,
          [groupId]: reordered
        }
      });
    } else {
      setNewDisease((prev) => ({
        ...prev,
        groups: {
          ...prev.groups,
          [groupId]: reordered
        }
      }));
    }
  };

  // Funções para gerenciar Doenças SOS
  const handleAddDisease = async () => {
    if (savingDisease) return;
    setSavingDisease(true);
    setDiseaseError(null);
    setDiseaseInfo(null);
    try {
      const created = await sosService.createDisease(newDisease);
      setDiseases(prev => [...prev, created]);
      resetDiseaseForm();
      setShowDiseaseModal(false);
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao criar doença SOS:', error);
      const message = error instanceof Error ? error.message : 'Erro ao criar doença. Tente novamente.';
      setDiseaseError(message);
    } finally {
      setSavingDisease(false);
    }
  };

  const handleEditDisease = async () => {
    if (!editingDisease || savingDisease) return;
    setSavingDisease(true);
    setDiseaseError(null);
    setDiseaseInfo(null);
    try {
      const updated = await sosService.updateDisease(editingDisease.id, editingDisease);
      setDiseases(prev => prev.map(disease => (disease.id === updated.id ? updated : disease)));
      setEditingDisease(null);
      setShowDiseaseModal(false);
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao atualizar doença SOS:', error);
      const message = error instanceof Error ? error.message : 'Erro ao atualizar doença. Tente novamente.';
      setDiseaseError(message);
    } finally {
      setSavingDisease(false);
    }
  };

  const handleDeleteDisease = async (diseaseId: string) => {
    if (savingDisease) return;
    setSavingDisease(true);
    setDiseaseError(null);
    setDiseaseInfo(null);
    try {
      await sosService.deleteDisease(diseaseId);
      setDiseases(prev => prev.filter(disease => disease.id !== diseaseId));
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao deletar doença SOS:', error);
      const message = error instanceof Error ? error.message : 'Erro ao deletar doença. Tente novamente.';
      setDiseaseError(message);
    } finally {
      setSavingDisease(false);
    }
  };

  const handleAddGroupOption = async () => {
    if (!selectedDiseaseId || !selectedGroupId || savingDisease) return;
    const label = newGroupOption.label.trim();
    if (!label) return;

    const targetDisease = diseases.find(disease => disease.id === selectedDiseaseId);
    if (!targetDisease) return;

    const newOption: SOSOption = {
      id: `option_${Date.now()}`,
      label,
      description: newGroupOption.description?.trim() || ''
    };

    const updatedDisease: SOSDisease = {
      ...targetDisease,
      groups: {
        ...targetDisease.groups,
        [selectedGroupId]: [...(targetDisease.groups[selectedGroupId] || []), newOption]
      }
    };

    setSavingDisease(true);
    setDiseaseError(null);
    try {
      const updated = await sosService.updateDisease(updatedDisease.id, {
        groups: updatedDisease.groups
      });
      setDiseases(prev => prev.map(disease => (disease.id === updated.id ? updated : disease)));
      setNewGroupOption({ label: '', description: '' });
      setShowGroupOptionModal(false);
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao adicionar opção ao grupo SOS:', error);
      const message = error instanceof Error ? error.message : 'Erro ao adicionar opção. Tente novamente.';
      setDiseaseError(message);
    } finally {
      setSavingDisease(false);
    }
  };

  const resetDiseaseForm = () => {
    setNewDisease({
      name: '',
      description: '',
      active: true,
      groups: createEmptyGroups()
    });
    setEditingDisease(null);
    setDiseaseError(null);
    setDiseaseInfo(null);
  };

  const resetGroupOptionForm = () => {
    setNewGroupOption({
      label: '',
      description: ''
    });
    setSelectedDiseaseId('');
    setSelectedGroupId('');
  };

  const handleSeedSosDiseases = async () => {
    if (seedingSosDiseases) return;
    setSeedingSosDiseases(true);
    setDiseaseError(null);
    setDiseaseInfo('Importando doenças iniciais...');

    try {
      for (const disease of seedSosDiseases) {
        await sosService.createDisease({
          name: disease.name,
          description: disease.description,
          active: disease.active,
          groups: disease.groups
        });
      }

      const refreshed = await sosService.getDiseases();
      setDiseases(refreshed);
      setDiseaseInfo('Doenças iniciais importadas com sucesso.');
    } catch (error) {
      console.error('❌ [AdminDashboard] Erro ao importar doenças iniciais:', error);
      const message = error instanceof Error ? error.message : 'Erro ao importar doenças. Tente novamente.';
      setDiseaseError(message);
      setDiseaseInfo(null);
    } finally {
      setSeedingSosDiseases(false);
    }
  };

  const iconOptions = [
    'ri-star-line',
    'ri-heart-line',
    'ri-medicine-bottle-line',
    'ri-time-line',
    'ri-trophy-line',
    'ri-fire-line',
    'ri-check-line',
    'ri-close-line',
    'ri-add-line',
    'ri-subtract-line',
    'ri-gift-line',
    'ri-medal-line'
  ];

  const totalActiveRules = pointRules.filter(rule => rule.active).length;
  const totalActiveRemedies = remedies.length; // Usar estado carregado do banco
  const totalActiveFacts = facts.filter(fact => fact.active).length;
  const totalActiveQuestions = quizQuestions.length; // Usar estado carregado do banco

  const addToArrayField = (field: string, inputField: string) => {
    const value = tempInput[inputField as keyof typeof tempInput];
    if (value.trim()) {
      if (editingRemedy) {
        if (field === 'worse' || field === 'better') {
          setEditingRemedy(prev => (prev ? { ...prev, modalities: { ...prev.modalities, [field]: [...(prev.modalities[field as keyof typeof prev.modalities] || []), value.trim()] } } : null));
        } else {
          setEditingRemedy(prev => (prev ? { ...prev, [field]: [...((prev[field as keyof Remedy] as string[]) || []), value.trim()] } : null));
        }
      } else {
        if (field === 'worse' || field === 'better') {
          setNewRemedy(prev => ({
            ...prev,
            modalities: {
              ...prev.modalities,
              [field]: [...(prev.modalities[field as keyof typeof prev.modalities] || []), value.trim()]
            }
          }));
        } else {
          setNewRemedy(prev => ({
            ...prev,
            [field]: [...((prev[field as keyof NewRemedy] as string[]) || []), value.trim()]
          }));
        }
      }
      setTempInput(prev => ({ ...prev, [inputField]: '' }));
    }
  };

  const removeFromArrayField = (field: string, index: number) => {
    if (editingRemedy) {
      if (field === 'worse' || field === 'better') {
        setEditingRemedy(prev => (prev ? { ...prev, modalities: { ...prev.modalities, [field]: (prev.modalities[field as keyof typeof prev.modalities] || []).filter((_, i) => i !== index) } } : null));
      } else {
        setEditingRemedy(prev => (prev ? { ...prev, [field]: ((prev[field as keyof Remedy] as string[]) || []).filter((_, i) => i !== index) } : null));
      }
    } else {
      if (field === 'worse' || field === 'better') {
        setNewRemedy(prev => ({
          ...prev,
          modalities: {
            ...prev.modalities,
            [field]: (prev.modalities[field as keyof typeof prev.modalities] || []).filter((_, i) => i !== index)
          }
        }));
      } else {
        setNewRemedy(prev => ({
          ...prev,
          [field]: ((prev[field as keyof NewRemedy] as string[]) || []).filter((_, i) => i !== index)
        }));
      }
    }
  };

  const getCategoryRemedyColor = (category: string) => {
    switch (category) {
      case 'plant':
        return 'bg-green-100 text-green-600 border-green-200';
      case 'mineral':
        return 'bg-blue-100 text-blue-600 border-blue-200';
      case 'animal':
        return 'bg-orange-100 text-orange-600 border-orange-200';
      case 'nosode':
        return 'bg-purple-100 text-purple-600 border-purple-200';
      case 'sarcodio':
        return 'bg-pink-100 text-pink-600 border-pink-200';
      case 'autotherapeutic':
        return 'bg-indigo-100 text-indigo-600 border-indigo-200';
      case 'isotherapeutic':
        return 'bg-cyan-100 text-cyan-600 border-cyan-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getCategoryRemedyIcon = (category: string) => {
    switch (category) {
      case 'plant':
        return 'ri-leaf-line';
      case 'mineral':
        return 'ri-stone-line';
      case 'animal':
        return 'ri-bug-line';
      case 'nosode':
        return 'ri-flask-line';
      case 'sarcodio':
        return 'ri-heart-pulse-line';
      case 'autotherapeutic':
        return 'ri-user-heart-line';
      case 'isotherapeutic':
        return 'ri-focus-3-line';
      default:
        return 'ri-medicine-bottle-line';
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar arquivo
      const validation = imageUploadService.validateImageFile(file);
      if (!validation.valid) {
        toast.warning(validation.error ?? 'Arquivo inválido');
        event.target.value = '';
        return;
      }

      try {
        // Fazer upload para Supabase Storage
        const remedyName = editingRemedy ? editingRemedy.name : newRemedy.name;
        if (!remedyName) {
          toast.warning('Por favor, preencha o nome do remédio antes de fazer upload da imagem');
          event.target.value = '';
          return;
        }

        // Preview local enquanto faz upload
        const previewUrl = URL.createObjectURL(file);
        if (editingRemedy) {
          setEditingRemedy(prev => (prev ? { ...prev, image: previewUrl } : null));
        } else {
          setNewRemedy(prev => ({ ...prev, image: previewUrl }));
        }

        console.log('📤 [AdminDashboard] Fazendo upload da imagem...');
        const uploadedUrl = await imageUploadService.uploadRemedyImage(file, remedyName);
        console.log('✅ [AdminDashboard] Upload concluído:', uploadedUrl);

        // Atualizar com URL real do Supabase
        if (editingRemedy) {
          setEditingRemedy(prev => (prev ? { ...prev, image: uploadedUrl } : null));
        } else {
          setNewRemedy(prev => ({ ...prev, image: uploadedUrl }));
        }
      } catch (error: any) {
        console.error('❌ [AdminDashboard] Erro no upload:', error);
        toast.error(`Erro ao fazer upload da imagem: ${error.message}`);
      }
    }
    event.target.value = '';
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const loadPushUsers = useCallback(async () => {
    try {
      setLoadingPushUsers(true);
      const list = await pushService.listPushUsers();
      setPushUsers(list);
      if (list.length) {
        setTestPushUserId((prev) => (prev ? prev : list[0].user_id));
      }
    } catch (error) {
      console.error('[AdminDashboard] Falha ao carregar usuários com token:', error);
    } finally {
      setLoadingPushUsers(false);
    }
  }, []);

  useEffect(() => { loadPushUsers(); }, [loadPushUsers]);

  const handleTestPush = async () => {
    try {
      const userId = testPushUserId.trim();
      if (!userId) {
        toast.warning('Informe o user_id do destinatário (com token ativo).');
        return;
      }

      const title = (pushTitle || 'Teste de notificação').trim();
      const body = (pushBody || 'Se você recebeu, está tudo certo!').trim();
      const res = await pushService.send({ userId, title, body });

      toast.success(`Push enviado: ${res.success} ok, ${res.failure} falha(s). Enviados: ${res.sent}`);
      if (res.pruned) toast.info(`Tokens inválidos removidos: ${res.pruned}`);
    } catch (e: any) {
      console.error('[AdminDashboard] Erro ao enviar push de teste:', e);
      toast.error(e?.message || 'Erro ao enviar push de teste');
    }
  };

  const handleBroadcastPush = async () => {
    try {
      if (!isAdmin) {
        toast.error('Apenas administradores podem enviar broadcast.');
        return;
      }
      const title = (pushTitle || '').trim();
      const body = (pushBody || '').trim();
      if (!title || !body) {
        toast.warning('Informe título e mensagem.');
        return;
      }
      const confirmed = window.confirm('Enviar esta mensagem para TODOS os usuários com token?');
      if (!confirmed) return;
      const res = await pushService.broadcast(title, body);
      toast.success(`Broadcast: ${res.success} ok, ${res.failure} falha(s). Enviados: ${res.sent}`);
      if (res.pruned) toast.info(`Tokens inválidos removidos: ${res.pruned}`);
    } catch (e: any) {
      console.error('[AdminDashboard] Erro ao enviar broadcast:', e);
      toast.error(e?.message || 'Erro ao enviar broadcast');
    }
  };

  // Se não for admin, não renderizar conteúdo (efeito já redireciona)
  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-purple-600 px-6 py-8 pt-12 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-white text-xl"></i>
          </button>
          <h1 className="text-xl font-bold text-white" style={{
            textShadow: '2px 4px 8px rgba(0, 0, 0, 0.6), 0px 2px 4px rgba(0, 0, 0, 0.3)'
          }}>Painel Administrativo</h1>
          <button 
            onClick={() => signOut()}
            className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center hover:bg-white/30 transition-colors"
            title="Sair"
          >
            <i className="ri-logout-box-line text-white text-xl"></i>
          </button>
        </div>

        <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Sistema de Pontuação</h2>
            <p className="text-purple-100 text-sm">Gerenciar regras e bônus de gamificação</p>
          </div>
        </div>
      </div>

      {/* Seção de Resumo - Estatísticas do Sistema */}
      <div className="px-6 py-6">
        <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Resumo do Sistema</h2>
              <p className="text-sm text-gray-600 mt-1">Visão geral das configurações e dados ativos</p>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
              <input
                type="text"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                placeholder="Título da mensagem"
                className="px-3 py-2 rounded-xl border border-gray-200 text-sm w-full md:w-60 min-w-0 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              />
              <input
                type="text"
                value={pushBody}
                onChange={(e) => setPushBody(e.target.value)}
                placeholder="Mensagem"
                className="px-3 py-2 rounded-xl border border-gray-200 text-sm w-full md:w-80 min-w-0 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              />
              {pushUsers.length > 0 ? (
                <select
                  value={testPushUserId}
                  onChange={(e) => setTestPushUserId(e.target.value)}
                  className="px-3 py-2 rounded-xl border border-gray-200 text-sm w-full md:w-72 min-w-0 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                >
                  {pushUsers.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.user_id} — {u.count} token(s)
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={testPushUserId}
                  onChange={(e) => setTestPushUserId(e.target.value)}
                  placeholder="user_id destino (sem tokens cadastrados)"
                  className="px-3 py-2 rounded-xl border border-gray-200 text-sm w-full md:w-72 min-w-0 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                />
              )}
              <button
                type="button"
                onClick={loadPushUsers}
                className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm border border-gray-200 shrink-0"
                title="Recarregar lista de usuários com token"
                disabled={loadingPushUsers}
              >
                {loadingPushUsers ? 'Atualizando...' : 'Atualizar lista'}
              </button>
              <button
                type="button"
                onClick={handleTestPush}
                className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm shadow shrink-0"
                title="Enviar notificação push de teste para o usuário selecionado"
              >
                Enviar teste
              </button>
              <button
                type="button"
                onClick={handleBroadcastPush}
                className="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm shadow shrink-0"
                title="Enviar push para todos os usuários com token"
              >
                Enviar para todos
              </button>
              <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl items-center justify-center">
                <i className="ri-dashboard-3-line text-white text-xl"></i>
              </div>
            </div>
          </div>

          {/* Estatísticas Principais - Grid Responsivo */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            {/* Regras de Pontuação */}
            <button
              type="button"
              onClick={() => scrollToSection(rulesSectionRef.current)}
              className="w-full h-full bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-4 border border-emerald-200 text-left transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-300 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <i className="ri-list-check-2 text-white text-lg"></i>
                </div>
                <span className="text-2xl font-bold text-emerald-700">{totalActiveRules}</span>
              </div>
              <h3 className="font-semibold text-emerald-800 mb-1">Regras Ativas</h3>
              <p className="text-xs text-emerald-600">Sistema de pontuação</p>
            </button>

            {/* Remédios */}
            <button
              type="button"
              onClick={() => scrollToSection(remediesSectionRef.current)}
              className="w-full h-full bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-4 border border-teal-200 text-left transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-300 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
                  <i className="ri-capsule-line text-white text-lg"></i>
                </div>
                <span className="text-2xl font-bold text-teal-700">{totalActiveRemedies}</span>
              </div>
              <h3 className="font-semibold text-teal-800 mb-1">Remédios</h3>
              <p className="text-xs text-teal-600">Biblioteca homeopática</p>
            </button>

            {/* Curiosidades */}
            <button
              type="button"
              onClick={() => scrollToSection(factsSectionRef.current)}
              className="w-full h-full bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-4 border border-yellow-200 text-left transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-300 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <i className="ri-lightbulb-line text-white text-lg"></i>
                </div>
                <span className="text-2xl font-bold text-yellow-700">{totalActiveFacts}</span>
              </div>
              <h3 className="font-semibold text-yellow-800 mb-1">Curiosidades</h3>
              <p className="text-xs text-yellow-600">Conteúdo educativo</p>
            </button>

            {/* Quiz */}
            <button
              type="button"
              onClick={() => scrollToSection(quizSectionRef.current)}
              className="w-full h-full bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-4 border border-indigo-200 text-left transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-300 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <i className="ri-question-line text-white text-lg"></i>
                </div>
                <span className="text-2xl font-bold text-indigo-700">{totalActiveQuestions}</span>
              </div>
              <h3 className="font-semibold text-indigo-800 mb-1">Perguntas Quiz</h3>
              <p className="text-xs text-indigo-600">Educação interativa</p>
            </button>

            {/* SOS */}
            <button
              type="button"
              onClick={() => scrollToSection(sosSectionRef.current)}
              className="w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4 border border-red-200 text-left transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-300 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-white text-lg"></i>
                </div>
                <span className="text-2xl font-bold text-red-700">{diseases.length}</span>
              </div>
              <h3 className="font-semibold text-red-800 mb-1">Doenças SOS</h3>
              <p className="text-xs text-red-600">Enfermidades cadastradas</p>
            </button>

            {/* Badges */}
            <button
              type="button"
              onClick={() => scrollToSection(badgesSectionRef.current)}
              className="w-full h-full bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 border border-purple-200 text-left transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-300 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                  <i className="ri-award-line text-white text-lg"></i>
                </div>
                <span className="text-2xl font-bold text-purple-700">{badges.length}</span>
              </div>
              <h3 className="font-semibold text-purple-800 mb-1">Badges</h3>
              <p className="text-xs text-purple-600">Conquistas</p>
            </button>
          </div>

          {/* Informações do Sistema */}
          <div className="border-t border-gray-100 pt-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mr-3">
                  <i className="ri-user-line text-purple-600 text-lg"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">4 Usuários Ativos</p>
                  <p className="text-xs text-gray-600">Total de usuários no sistema</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700">Sistema Online</p>
                <p className="text-xs text-green-600 flex items-center justify-end">
                  <i className="ri-checkbox-circle-fill mr-1"></i>
                  Funcionando normalmente
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regras de Pontuação */}
      <div ref={rulesSectionRef} className="px-6 mb-6">
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Regras de Pontuação</h3>
              <button
                onClick={() => setShowAddRuleModal(true)}
                className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-add-line text-purple-600 text-lg"></i>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4 max-h-80 overflow-y-auto">
            {pointRules.map(rule => (
              <div key={rule.id} className={`border-2 rounded-2xl p-4 ${getCategoryBorder(rule.category)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1 min-w-0 mr-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 flex-shrink-0 ${getCategoryColor(rule.category)}`}>
                      <i className={`${rule.icon} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 mb-1">{rule.name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(rule.category)}`}>
                          {rule.category === 'positive' ? 'Positiva' : 'Negativa'}
                        </span>
                        <span className={`font-bold ${rule.points >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {rule.points >= 0 ? '+' : ''}{rule.points} pts
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleRuleStatus(rule.id)}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        rule.active ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      <i className={rule.active ? 'ri-check-line' : 'ri-close-line'}></i>
                    </button>
                    <button
                      onClick={() => {
                        setEditingRule(rule);
                        setShowEditRuleModal(true);
                      }}
                      className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center"
                    >
                      <i className="ri-edit-line text-blue-600 text-xs"></i>
                    </button>
                    <button
                      onClick={() => deleteRule(rule.id)}
                      className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center"
                    >
                      <i className="ri-delete-bin-line text-red-600 text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Biblioteca de Remédios */}
      <div ref={remediesSectionRef} className="px-6 mb-6">
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Biblioteca de Remédios</h3>
              <button
                onClick={() => {
                  setEditingRemedy(null);
                  resetRemedyForm();
                  setShowRemedyModal(true);
                }}
                className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-add-line text-teal-600 text-lg"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">Gerenciar informações dos remédios homeopáticos</p>
          </div>

          <div className="p-6 space-y-4 max-h-80 overflow-y-auto">
            {remedies.map(remedy => (
              <div key={remedy.id} className={`border-2 rounded-2xl p-4 ${getCategoryRemedyColor(remedy.category)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1 min-w-0 mr-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 flex-shrink-0 ${getCategoryRemedyColor(remedy.category)}`}>
                      {remedy.category === 'mineral' ? (
                        <IoDiamondOutline className="text-lg" />
                      ) : remedy.category === 'autotherapeutic' ? (
                        <FaPills className="text-lg" />
                      ) : (
                        <i className={`${getCategoryRemedyIcon(remedy.category)} text-lg`}></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 mb-1">{remedy.name}</h4>
                      <p className="text-sm text-gray-600 mb-2 italic">{remedy.scientificName}</p>
                      <p className="text-xs text-gray-500 line-clamp-2 mb-2">{remedy.description}</p>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryRemedyColor(remedy.category)}`}>
                          {remedy.category === 'plant'
                            ? 'Vegetal'
                            : remedy.category === 'mineral'
                            ? 'Mineral'
                            : remedy.category === 'animal'
                            ? 'Animal'
                            : remedy.category === 'nosode'
                            ? 'Nosódio'
                            : remedy.category === 'sarcodio'
                            ? 'Sarcódio'
                            : remedy.category === 'autotherapeutic'
                            ? 'Tautoterápico'
                            : remedy.category === 'isotherapeutic'
                            ? 'Isoterápico'
                            : 'Outro'}
                        </span>
                        <span className="text-xs text-gray-500">{(remedy.mainIndications || []).length} indicações</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => {
                        const remedyData: any = remedy;
                        setEditingRemedy({
                          ...remedy,
                          scientificName: remedyData.scientific_name || remedy.scientificName || '',
                          commonNames: remedyData.common_names || remedy.commonNames || [],
                          mainIndications: remedyData.main_indications || remedy.mainIndications || [],
                          mentalSymptoms: remedyData.mental_symptoms || remedy.mentalSymptoms || [],
                          physicalSymptoms: remedyData.physical_symptoms || remedy.physicalSymptoms || [],
                          modalities: {
                            worse: remedyData.modalities_worse || remedy.modalities?.worse || [],
                            better: remedyData.modalities_better || remedy.modalities?.better || []
                          },
                          image: remedyData.image_url || remedy.image || ''
                        });
                        setShowRemedyModal(true);
                      }}
                      className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center"
                    >
                      <i className="ri-edit-line text-blue-600 text-xs"></i>
                    </button>
                    <button
                      onClick={() => deleteRemedy(remedy.id)}
                      disabled={deletingRemedyId === remedy.id}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        deletingRemedyId === remedy.id
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-100 hover:bg-red-200'
                      }`}
                      title={deletingRemedyId === remedy.id ? 'Deletando...' : 'Deletar remédio'}
                    >
                      {deletingRemedyId === remedy.id ? (
                        <i className="ri-loader-4-line animate-spin text-xs"></i>
                      ) : (
                        <i className="ri-delete-bin-line text-red-600 text-xs"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {remedies.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-capsule-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-sm">Nenhum remédio cadastrado ainda</p>
                <p className="text-gray-400 text-xs mt-1">Clique no botão + para adicionar o primeiro remédio</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Curiosidades sobre Homeopatia */}
      <div ref={factsSectionRef} className="px-6 mb-6">
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Curiosidades sobre Homeopatia</h3>
              <button
                onClick={() => {
                  setEditingFact(null);
                  resetFactForm();
                  setShowFactModal(true);
                }}
                className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-add-line text-yellow-600 text-lg"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">Gerenciar curiosidades educativas sobre homeopatia</p>
          </div>

          <div className="p-6 space-y-4 max-h-80 overflow-y-auto">
            {facts.map(fact => (
              <div key={fact.id} className="border-2 border-yellow-200 rounded-2xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1 min-w-0 mr-4">
                    <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-lightbulb-line text-yellow-600 text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 mb-1">{fact.title}</h4>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{fact.content}</p>
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-600 rounded-full text-xs font-medium">
                          {fact.category === 'general' ? 'Geral' : 
                           fact.category === 'history' ? 'História' :
                           fact.category === 'principles' ? 'Princípios' :
                           fact.category === 'remedies' ? 'Remédios' :
                           fact.category === 'research' ? 'Pesquisa' : 'Outros'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(fact.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleFactStatus(fact.id)}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        fact.active ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      <i className={fact.active ? 'ri-check-line' : 'ri-close-line'}></i>
                    </button>
                    <button
                      onClick={() => {
                        setEditingFact(fact);
                        setShowFactModal(true);
                      }}
                      className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center"
                    >
                      <i className="ri-edit-line text-blue-600 text-xs"></i>
                    </button>
                    <button
                      onClick={() => deleteFact(fact.id)}
                      className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center"
                    >
                      <i className="ri-delete-bin-line text-red-600 text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {facts.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-lightbulb-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-sm">Nenhuma curiosidade cadastrada ainda</p>
                <p className="text-gray-400 text-xs mt-1">Clique no botão + para adicionar a primeira curiosidade</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quiz sobre Homeopatia */}
      <div ref={quizSectionRef} className="px-6 mb-6">
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Quiz sobre Homeopatia</h3>
                <p className="text-sm text-gray-600 mt-1">Gerenciar perguntas educativas do quiz diário</p>
              </div>
              <button
                onClick={() => setShowQuizPanel(true)}
                className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors flex items-center gap-2"
              >
                <i className="ri-question-line"></i>
                Gerenciar Quiz
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-question-line text-indigo-600 text-xl"></i>
                </div>
                <p className="text-2xl font-bold text-gray-800">{totalActiveQuestions}</p>
                <p className="text-sm text-gray-600">Perguntas Ativas</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-book-line text-blue-600 text-xl"></i>
                </div>
                <p className="text-2xl font-bold text-gray-800">{quizQuestions.filter(q => q.category === 'basic_principles').length}</p>
                <p className="text-sm text-gray-600">Princípios Básicos</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-medicine-bottle-line text-green-600 text-xl"></i>
                </div>
                <p className="text-2xl font-bold text-gray-800">{quizQuestions.filter(q => q.category === 'remedies').length}</p>
                <p className="text-sm text-gray-600">Medicamentos</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-flask-line text-purple-600 text-xl"></i>
                </div>
                <p className="text-2xl font-bold text-gray-800">{quizQuestions.filter(q => q.category === 'potencies').length}</p>
                <p className="text-sm text-gray-600">Potências</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-time-line text-orange-600 text-xl"></i>
                </div>
                <p className="text-2xl font-bold text-gray-800">{quizQuestions.filter(q => q.category === 'history').length}</p>
                <p className="text-sm text-gray-600">História</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar/Editar Regra */}
      {(showAddRuleModal || showEditRuleModal) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {showAddRuleModal ? 'Nova Regra' : 'Editar Regra'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddRuleModal(false);
                    setShowEditRuleModal(false);
                    setEditingRule(null);
                  }}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Regra</label>
                <input
                  type="text"
                  value={showAddRuleModal ? newRule.name : editingRule?.name || ''}
                  onChange={e => {
                    if (showAddRuleModal) setNewRule(prev => ({ ...prev, name: e.target.value }));
                    else if (editingRule) setEditingRule(prev => (prev ? { ...prev, name: e.target.value } : null));
                  }}
                  placeholder="Ex: Dose tomada no horário"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-purple-300 focus:outline-none"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <textarea
                  value={showAddRuleModal ? newRule.description : editingRule?.description || ''}
                  onChange={e => {
                    if (showAddRuleModal) setNewRule(prev => ({ ...prev, description: e.target.value }));
                    else if (editingRule) setEditingRule(prev => (prev ? { ...prev, description: e.target.value } : null));
                  }}
                  placeholder="Descreva quando esta regra deve ser aplicada"
                  className="w-full h-20 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:border-purple-300 focus:outline-none"
                />
              </div>

              {/* Pontos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pontos</label>
                <input
                  type="number"
                  value={showAddRuleModal ? newRule.points : editingRule?.points || 0}
                  onChange={e => {
                    const points = parseInt(e.target.value) || 0;
                    if (showAddRuleModal) setNewRule(prev => ({ ...prev, points }));
                    else if (editingRule) setEditingRule(prev => (prev ? { ...prev, points } : null));
                  }}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-purple-300 focus:outline-none"
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'positive', label: 'Positiva', color: 'emerald' },
                    { key: 'negative', label: 'Negativa', color: 'red' }
                  ].map(cat => (
                    <button
                      key={cat.key}
                      onClick={() => {
                        if (showAddRuleModal) setNewRule(prev => ({ ...prev, category: cat.key as any }));
                        else if (editingRule) setEditingRule(prev => (prev ? { ...prev, category: cat.key as any } : null));
                      }}
                      className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                        (showAddRuleModal ? newRule.category : editingRule?.category) === cat.key
                          ? `bg-${cat.color}-100 text-${cat.color}-600 border-2 border-${cat.color}-300`
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ícone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ícone</label>
                <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto">
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      onClick={() => {
                        if (showAddRuleModal) setNewRule(prev => ({ ...prev, icon }));
                        else if (editingRule) setEditingRule(prev => (prev ? { ...prev, icon } : null));
                      }}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                        (showAddRuleModal ? newRule.icon : editingRule?.icon) === icon
                          ? 'bg-purple-100 border-2 border-purple-300'
                          : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200'
                      }`}
                    >
                      <i className={`${icon} text-lg`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="p-6 border-t border-gray-100">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAddRuleModal(false);
                    setShowEditRuleModal(false);
                    setEditingRule(null);
                  }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={showAddRuleModal ? handleAddRule : handleEditRule}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-2xl font-medium"
                >
                  {showAddRuleModal ? 'Adicionar' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Modal Adicionar/Editar Remédio */}
      {showRemedyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">{editingRemedy ? 'Editar Remédio' : 'Novo Remédio'}</h3>
                <button
                  onClick={() => {
                    setShowRemedyModal(false);
                    setEditingRemedy(null);
                  }}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Campo de Ícone/Imagem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Ícone do Remédio *</label>

                {/* Preview da imagem atual */}
                <div className="mb-4">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-100 mx-auto flex items-center justify-center">
                    {(editingRemedy?.image || newRemedy.image) ? (
                      <img src={editingRemedy?.image || newRemedy.image} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-image-line text-gray-400 text-2xl"></i>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">Preview do ícone</p>
                </div>

                {/* Input de arquivo oculto */}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

                {/* Seleção automática e manual */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-600 mb-3">Opções de imagem:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const category = editingRemedy ? editingRemedy.category : newRemedy.category;
                        const name = editingRemedy ? editingRemedy.name : newRemedy.name;
                        const scientificName = editingRemedy ? editingRemedy.scientificName : newRemedy.scientificName;
                        let prompt = '';
                        switch (category) {
                          case 'plant':
                            prompt = `icon, Realistic botanical illustration, ${name || 'medicinal plant'} ${scientificName || 'homeopathic remedy'}, high-detail photorealistic rendering, prominent main subject plant, clear and sharp, the plant should take up 70% of the frame, isolated on white background, centered composition, soft natural lighting, subtle shadows, scientific illustration style`;
                            break;
                          case 'mineral':
                            prompt = `icon, 3D realistic mineral crystal, ${name || 'healing mineral'} crystal structure, high-quality geometric details, prominent crystalline formation, clear and sharp mineral textures, the crystal should take up 70% of the frame, isolated on white background, centered composition, soft lighting with reflections`;
                            break;
                          case 'animal':
                            prompt = `icon, Realistic animal illustration, ${name || 'homeopathic animal remedy'} source creature, high-detail naturalistic rendering, prominent main animal subject, clear and sharp anatomical details, the animal should take up 70% of the frame, isolated on white background, centered composition, natural lighting`;
                            break;
                          case 'nosode':
                            prompt = `icon, 2.5D vector illustration, Laboratory flask with ${name || 'nosode preparation'}, high-quality scientific details, prominent main laboratory subject, clear and sharp glassware, the flask should take up 70% of the frame, isolated on white background, centered composition`;
                            break;
                          case 'sarcodio':
                            prompt = `icon, 3D medical illustration, Sarcodio ${name || 'biological remedy'} cellular structure, vibrant colors with soft gradients, minimalist medical design, smooth rounded biological shapes, subtle shading, no outlines, centered composition, isolated on white background, scientific and friendly aesthetic`;
                            break;
                          case 'autotherapeutic':
                            prompt = `icon, 2.5D vector illustration, Autotherapeutic ${name || 'auto-immune remedy'} representation, high-quality medical details, prominent main therapeutic subject, clear and sharp, the symbol should take up 70% of the frame, isolated on white background, centered composition, soft lighting, medical style`;
                            break;
                          case 'isotherapeutic':
                            prompt = `icon, 3D medical illustration, Isotherapeutic ${name || 'isopathy remedy'} cellular structure, vibrant colors with soft gradients, minimalist medical design, smooth rounded biological shapes, subtle shading, no outlines, centered composition, isolated on white background, scientific and friendly aesthetic`;
                            break;
                          default:
                            prompt = `icon, 2.5D vector illustration, Homeopathic remedy ${name || 'medicine'}, high-quality details, prominent main subject, clear and sharp, the icon should take up 70% of the frame, isolated on white background, centered composition, soft lighting, no text, medical style`;
                        }
                        const imageUrl = `https://readdy.ai/api/search-image?query=${encodeURIComponent(prompt)}&width=300&height=200&seq=${Date.now()}&orientation=landscape`;
                        if (editingRemedy) {
                          setEditingRemedy(prev => (prev ? { ...prev, image: imageUrl } : null));
                        } else {
                          setNewRemedy(prev => ({ ...prev, image: imageUrl }));
                        }
                      }}
                      className="py-2 px-3 bg-teal-100 text-teal-600 rounded-xl text-xs font-medium hover:bg-teal-200 transition-colors"
                    >
                      <i className="ri-magic-line mr-1"></i> Auto Gerar
                    </button>

                    <button type="button" onClick={openFileSelector} className="py-2 px-3 bg-blue-100 text-blue-600 rounded-xl text-xs font-medium hover:bg-blue-200 transition-colors">
                      <i className="ri-upload-2-line mr-1"></i> Selecionar
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (editingRemedy) {
                          setEditingRemedy(prev => (prev ? { ...prev, image: '' } : null));
                        } else {
                          setNewRemedy(prev => ({ ...prev, image: '' }));
                        }
                      }}
                      className="py-2 px-3 bg-red-100 text-red-600 rounded-xl text-xs font-medium hover:bg-red-200 transition-colors"
                    >
                      <i className="ri-delete-bin-line mr-1"></i> Limpar
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Clique em "Selecionar" para escolher uma imagem do seu dispositivo
                  </p>
                </div>

                {/* Campo manual URL */}
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Ou insira URL personalizada:</p>
                  <input
                    type="url"
                    value={editingRemedy ? editingRemedy.image : newRemedy.image}
                    onChange={e => {
                      if (editingRemedy) setEditingRemedy(prev => (prev ? { ...prev, image: e.target.value } : null));
                      else setNewRemedy(prev => ({ ...prev, image: e.target.value }));
                    }}
                    placeholder="https://exemplo.com/imagem.jpg"
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL de imagem, arquivo selecionado, ou deixe em branco para usar ícone padrão</p>
                </div>

                {/* Galeria de ícones por categoria */}
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-600 mb-3">Ou escolha um ícone da galeria:</p>
                  <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-xl">
                    {(() => {
                      const currentCategory = editingRemedy ? editingRemedy.category : newRemedy.category;
                      const iconsByCategory: Record<string, string[]> = {
                        plant: [
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20botanical%20plant%2C%20green%20medicinal%20herb%2C%20high-detail%203D%20rendering%2C%20prominent%20main%20plant%20subject%2C%20clear%20and%20sharp%2C%20the%20plant%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20soft%20lighting%2C%20subtle%20shadows%2C%20botanical%20illustration%20style&width=300&height=200&seq=plant1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20leaf%2C%20medicinal%20plant%20leaf%2C%20high-quality%20botanical%20details%2C%20prominent%20main%20subject%2C%20clear%20and%20sharp%2C%20the%20leaf%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20soft%20lighting%2C%20no%20shadows%2C%20botanical%20style&width=300&height=200&seq=plant2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20flower%20illustration%2C%20medicinal%20flower%20bloom%2C%20high-detail%20photorealistic%20rendering%2C%20prominent%20main%20flower%20subject%2C%20clear%20and%20sharp%20botanical%20details%2C%20the%20flower%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting&width=300&height=200&seq=plant3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20herb%20illustration%2C%20healing%20herb%20bundle%2C%20vibrant%20green%20colors%20with%20soft%20gradients%2C%20minimalist%20botanical%20design%2C%20smooth%20rounded%20plant%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20natural%20and%20friendly%20aesthetic&width=300&height=200&seq=plant4&orientation=landscape'
                        ],
                        mineral: [
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20realistic%20crystal%2C%20blue%20healing%20mineral%20crystal%2C%20high-quality%20geometric%20details%2C%20prominent%20crystalline%20formation%2C%20clear%20and%20sharp%20mineral%20textures%2C%20the%20crystal%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20soft%20lighting%20with%20reflections&width=300&height=200&seq=mineral1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20stone%20formation%2C%20white%20calcium%20mineral%2C%20high-detail%203D%20rendering%2C%20prominent%20main%20mineral%20subject%2C%20clear%20and%20sharp%20crystalline%20structure%2C%20the%20stone%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20soft%20lighting&width=300&height=200&seq=mineral2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20geometric%20crystal%2C%20mineral%20formation%2C%20high-quality%20details%2C%20prominent%20main%20crystalline%20subject%2C%20clear%20and%20sharp%20edges%2C%20the%20crystal%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition&width=300&height=200&seq=mineral3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20salt%20crystal%2C%20white%20mineral%20salt%20formation%2C%20vibrant%20colors%20with%20soft%20gradients%2C%20minimalist%20crystalline%20design%2C%20smooth%20geometric%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20clean%20mineral%20aesthetic&width=300&height=200&seq=mineral4&orientation=landscape'
                        ],
                        animal: [
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20bee%20illustration%2C%20honeybee%20insect%2C%20high-detail%20naturalistic%20rendering%2C%20prominent%20main%20bee%20subject%2C%20clear%20and%20sharp%20anatomical%20details%2C%20the%20bee%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20natural%20lighting&width=300&height=200&seq=animal1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20spider%20silhouette%2C%20medicinal%20spider%2C%20high-quality%20zoological%20details%2C%20prominent%20main%20arachnid%20subject%2C%20clear%20and%20sharp%2C%20the%20spider%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition&width=300&height=200&seq=animal2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20snake%20illustration%2C%20healing%20serpent%2C%20high-detail%20reptilian%20rendering%2C%20prominent%20main%20snake%20subject%2C%20clear%20and%20sharp%20scales%2C%20the%20snake%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20natural%20lighting&width=300&height=200&seq=animal3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20butterfly%20illustration%2C%20colorful%20healing%20butterfly%2C%20vibrant%20colors%20with%20soft%20gradients%2C%20minimalist%20insect%20design%2C%20smooth%20rounded%20wings%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20natural%20and%20friendly%20aesthetic&width=300&height=200&seq=animal4&orientation=landscape'
                        ],
                        nosode: [
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Laboratory%20flask%20with%20purple%20liquid%2C%20nosode%20preparation%2C%20high-quality%20scientific%20details%2C%20prominent%20main%20flask%20subject%2C%20clear%20and%20sharp%20glassware%2C%20the%20flask%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition&width=300&height=200&seq=nosode1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20test%20tube%2C%20medical%20laboratory%20tube%2C%20high-detail%20scientific%20rendering%2C%20prominent%20main%20tube%20subject%2C%20clear%20and%20sharp%20glass%2C%20the%20tube%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20laboratory%20lighting&width=300&height=200&seq=nosode2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20microscope%20illustration%2C%20scientific%20microscope%2C%20vibrant%20colors%20with%20soft%20gradients%2C%20minimalist%20laboratory%20design%2C%20smooth%20rounded%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20scientific%20aesthetic&width=300&height=200&seq=nosode3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20laboratory%20beaker%2C%20scientific%20glassware%2C%20high-quality%20details%2C%20prominent%20main%20beaker%20subject%2C%20clear%20and%20sharp%2C%20the%20beaker%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20medical%20laboratory%20style&width=300&height=200&seq=nosode4&orientation=landscape'
                        ],
                        sarcodio: [
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20cellular%20structure%2C%20sarcodio%20biological%20remedy%2C%20vibrant%20pink%20tones%20with%20soft%20gradients%2C%20minimalist%20biological%20design%2C%20smooth%20rounded%20cellular%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20medical%20aesthetic&width=300&height=200&seq=sarcodio1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20DNA%20helix%2C%20sarcodio%20category%2C%20high-detail%20biological%20rendering%2C%20prominent%20main%20DNA%20subject%2C%20clear%20and%20sharp%20molecular%20details%2C%20the%20helix%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20scientific%20lighting&width=300&height=200&seq=sarcodio2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20heart%20pulse%2C%20sarcodio%20symbol%2C%20high-quality%20medical%20details%2C%20prominent%20main%20pulse%20subject%2C%20clear%20and%20sharp%2C%20the%20symbol%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20medical%20style&width=300&height=200&seq=sarcodio3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20hormone%20molecule%2C%20sarcodio%20category%2C%20vibrant%20pink%20colors%20with%20soft%20gradients%2C%20minimalist%20molecular%20design%2C%20smooth%20rounded%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20medical%20aesthetic&width=300&height=200&seq=sarcodio4&orientation=landscape'
                        ],
                        autotherapeutic: [
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20blood%20cell%20illustration%2C%20red%20blood%20cells%2C%20autotherapeutic%20remedy%2C%20vibrant%20indigo%20colors%20with%20soft%20gradients%2C%20minimalist%20cellular%20design%2C%20smooth%20rounded%20blood%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20medical%20aesthetic&width=300&height=200&seq=auto1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20human%20silhouette%2C%20patient%20outline%2C%20high-detail%20medical%20rendering%2C%20prominent%20main%20human%20subject%2C%20clear%20and%20sharp%20anatomical%20outline%2C%20the%20figure%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20medical%20lighting&width=300&height=200&seq=auto2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20heart%20with%20user%2C%20personalized%20medicine%20symbol%2C%20high-quality%20medical%20details%2C%20prominent%20main%20heart%20subject%2C%20clear%20and%20sharp%2C%20the%20symbol%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20medical%20style&width=300&height=200&seq=auto3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20immune%20system%20cell%2C%20white%20blood%20cell%20illustration%2C%20autotherapeutic%20immune%20cell%2C%20vibrant%20indigo%20colors%20with%20soft%20gradients%2C%20minimalist%20cellular%20design%2C%20smooth%20rounded%20immune%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background&width=300&height=200&seq=auto4&orientation=landscape'
                        ],
                        isotherapeutic: [
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20medical%20illustration%2C%20isotherapeutic%20remedy%20structure%2C%20vibrant%20cyan%20tones%20with%20soft%20gradients%2C%20minimalist%20isopathy%20design%2C%20smooth%20rounded%20cellular%20forms%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20scientific%20aesthetic&width=300&height=200&seq=isoterapico1&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%20Realistic%20immune%20cell%2C%20isopathy%20category%2C%20high-detail%20biological%20rendering%2C%20prominent%20main%20cell%20subject%2C%20clear%20and%20sharp%20details%2C%20the%20cell%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20scientific%20lighting&width=300&height=200&seq=isoterapico2&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%202.5D%20vector%20illustration%2C%20Simple%20molecular%20structure%2C%20isotherapeutic%20symbol%2C%20high-quality%20medical%20details%2C%20prominent%20main%20molecule%20subject%2C%20clear%20and%20sharp%2C%20the%20symbol%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20medical%20style&width=300&height=200&seq=isoterapico3&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=icon%2C%203D%20capsule%20illustration%2C%20isotherapeutic%20remedy%20capsule%2C%20vibrant%20cyan%20colors%20with%20soft%20gradients%2C%20minimalist%20pharmaceutical%20design%2C%20smooth%20rounded%20shapes%2C%20subtle%20shading%2C%20centered%20composition%2C%20isolated%20on%20white%20background%2C%20clinical%20aesthetic&width=300&height=200&seq=isoterapico4&orientation=landscape'
                        ]
                      };
                      return iconsByCategory[currentCategory] || iconsByCategory.plant;
                    })().map((iconUrl, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          if (editingRemedy) {
                            setEditingRemedy(prev => (prev ? { ...prev, image: iconUrl } : null));
                          } else {
                            setNewRemedy(prev => ({ ...prev, image: iconUrl }));
                          }
                        }}
                        className={`w-12 h-12 rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                          (editingRemedy ? editingRemedy.image : newRemedy.image) === iconUrl
                            ? 'border-teal-300 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img src={iconUrl} alt={`Ícone ${index + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Nome do Remédio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Remédio *</label>
                <input
                  type="text"
                  value={editingRemedy ? editingRemedy.name : newRemedy.name}
                  onChange={e => {
                    if (editingRemedy) setEditingRemedy(prev => (prev ? { ...prev, name: e.target.value } : null));
                    else setNewRemedy(prev => ({ ...prev, name: e.target.value }));
                  }}
                  placeholder="Ex: Arnica Montana"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                />
              </div>

              {/* Nome Científico */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Científico *</label>
                <input
                  type="text"
                  value={editingRemedy ? editingRemedy.scientificName : newRemedy.scientificName}
                  onChange={e => {
                    if (editingRemedy) setEditingRemedy(prev => (prev ? { ...prev, scientificName: e.target.value } : null));
                    else setNewRemedy(prev => ({ ...prev, scientificName: e.target.value }));
                  }}
                  placeholder="Ex: Arnica montana L."
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <div className="grid grid-cols-3 gap-2">
                {[
                    { key: 'plant', label: 'Vegetal', icon: 'ri-leaf-line' },
                    { key: 'mineral', label: 'Mineral', icon: 'ri-stone-line' },
                    { key: 'animal', label: 'Animal', icon: 'ri-bug-line' },
                    { key: 'nosode', label: 'Nosódio', icon: 'ri-flask-line' },
                    { key: 'sarcodio', label: 'Sarcódio', icon: 'ri-heart-pulse-line' },
                    { key: 'autotherapeutic', label: 'Tautoterápico', icon: 'ri-user-heart-line' },
                    { key: 'isotherapeutic', label: 'Isoterápico', icon: 'ri-focus-3-line' }
                  ].map(cat => (
                    <button
                      key={cat.key}
                      onClick={() => {
                        if (editingRemedy) setEditingRemedy(prev => (prev ? { ...prev, category: cat.key as any } : null));
                        else setNewRemedy(prev => ({ ...prev, category: cat.key as any }));
                      }}
                      className={`py-3 px-3 rounded-xl text-sm font-medium transition-colors inline-flex items-center ${
                        (editingRemedy ? editingRemedy.category : newRemedy.category) === cat.key
                          ? getCategoryRemedyColor(cat.key)
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                      }`}
                    >
                      <span>{cat.label}</span>
                      {cat.key === 'mineral' ? (
                        <IoDiamondOutline className="ml-2 align-middle" />
                      ) : cat.key === 'autotherapeutic' ? (
                        <FaPills className="ml-2 align-middle" />
                      ) : (
                        <i className={`${cat.icon} ml-2`}></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição *</label>
                <textarea
                  value={editingRemedy ? editingRemedy.description : newRemedy.description}
                  onChange={e => {
                    if (editingRemedy) setEditingRemedy(prev => (prev ? { ...prev, description: e.target.value } : null));
                    else setNewRemedy(prev => ({ ...prev, description: e.target.value }));
                  }}
                  placeholder="Descrição detalhada do remédio homeopático"
                  className="w-full h-20 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:border-teal-300 focus:outline-none"
                  maxLength={500}
                />
              </div>

              {/* Constituição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Constituição</label>
                <textarea
                  value={editingRemedy ? editingRemedy.constitution : newRemedy.constitution}
                  onChange={e => {
                    if (editingRemedy) setEditingRemedy(prev => (prev ? { ...prev, constitution: e.target.value } : null));
                    else setNewRemedy(prev => ({ ...prev, constitution: e.target.value }));
                  }}
                  placeholder="Tipo de pessoa/constituição para este remédio"
                  className="w-full h-16 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:border-teal-300 focus:outline-none"
                  maxLength={300}
                />
              </div>

              {/* Nomes Populares */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nomes Populares</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.commonName}
                    onChange={e => setTempInput(prev => ({ ...prev, commonName: e.target.value }))}
                    placeholder="Nome popular"
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                    onKeyDown={e => e.key === 'Enter' && addToArrayField('commonNames', 'commonName')}
                  />
                  <button
                    type="button"
                    onClick={() => addToArrayField('commonNames', 'commonName')}
                    className="px-4 py-3 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((editingRemedy ? editingRemedy.commonNames : newRemedy.commonNames) || []).map((name, index) => (
                    <span key={index} className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm flex items-center">
                      {name}
                      <button
                        type="button"
                        onClick={() => removeFromArrayField('commonNames', index)}
                        className="ml-2 text-blue-400 hover:text-blue-600"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Principais Indicações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Principais Indicações</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.indication}
                    onChange={e => setTempInput(prev => ({ ...prev, indication: e.target.value }))}
                    placeholder="Indicação terapêutica"
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                    onKeyDown={e => e.key === 'Enter' && addToArrayField('mainIndications', 'indication')}
                  />
                  <button
                    type="button"
                    onClick={() => addToArrayField('mainIndications', 'indication')}
                    className="px-4 py-3 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((editingRemedy ? editingRemedy.mainIndications : newRemedy.mainIndications) || []).map((indication, index) => (
                    <span key={index} className="bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full text-sm flex items-center">
                      {indication}
                      <button
                        type="button"
                        onClick={() => removeFromArrayField('mainIndications', index)}
                        className="ml-2 text-emerald-400 hover:text-emerald-600"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Sintomas Mentais */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sintomas Mentais</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.mentalSymptom}
                    onChange={e => setTempInput(prev => ({ ...prev, mentalSymptom: e.target.value }))}
                    placeholder="Sintoma mental/emocional"
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                    onKeyDown={e => e.key === 'Enter' && addToArrayField('mentalSymptoms', 'mentalSymptom')}
                  />
                  <button
                    type="button"
                    onClick={() => addToArrayField('mentalSymptoms', 'mentalSymptom')}
                    className="px-4 py-3 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((editingRemedy ? editingRemedy.mentalSymptoms : newRemedy.mentalSymptoms) || []).map((sym, index) => (
                    <span key={index} className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm flex items-center">
                      {sym}
                      <button
                        type="button"
                        onClick={() => removeFromArrayField('mentalSymptoms', index)}
                        className="ml-2 text-purple-400 hover:text-purple-600"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Sintomas Físicos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sintomas Físicos</label>
                <div className="flex gap-2 mb-1">
                  <input
                    type="text"
                    value={tempInput.physicalSymptom}
                    onChange={e => setTempInput(prev => ({ ...prev, physicalSymptom: e.target.value }))}
                    placeholder="Sintoma físico"
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                    onKeyDown={e => e.key === 'Enter' && addToArrayField('physicalSymptoms', 'physicalSymptom')}
                  />
                  <button
                    type="button"
                    onClick={() => addToArrayField('physicalSymptoms', 'physicalSymptom')}
                    className="px-4 py-3 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((editingRemedy ? editingRemedy.physicalSymptoms : newRemedy.physicalSymptoms) || []).map((sym, index) => (
                    <span key={index} className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm flex items-center">
                      {sym}
                      <button
                        type="button"
                        onClick={() => removeFromArrayField('physicalSymptoms', index)}
                        className="ml-2 text-blue-400 hover:text-blue-600"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Modalidades - Piora */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Piora com</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.worse}
                    onChange={e => setTempInput(prev => ({ ...prev, worse: e.target.value }))}
                    placeholder="Fatores que pioram"
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                    onKeyDown={e => e.key === 'Enter' && addToArrayField('worse', 'worse')}
                  />
                  <button
                    type="button"
                    onClick={() => addToArrayField('worse', 'worse')}
                    className="px-4 py-3 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((editingRemedy ? editingRemedy.modalities?.worse : newRemedy.modalities?.worse) || []).map((item, index) => (
                    <span key={index} className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm flex items-center">
                      {item}
                      <button
                        type="button"
                        onClick={() => removeFromArrayField('worse', index)}
                        className="ml-2 text-red-400 hover:text-red-600"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Modalidades - Melhora */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Melhora com</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.better}
                    onChange={e => setTempInput(prev => ({ ...prev, better: e.target.value }))}
                    placeholder="Fatores que melhoram"
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-teal-300 focus:outline-none"
                    onKeyDown={e => e.key === 'Enter' && addToArrayField('better', 'better')}
                  />
                  <button
                    type="button"
                    onClick={() => addToArrayField('better', 'better')}
                    className="px-4 py-3 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((editingRemedy ? editingRemedy.modalities?.better : newRemedy.modalities?.better) || []).map((item, index) => (
                    <span key={index} className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm flex items-center">
                      {item}
                      <button
                        type="button"
                        onClick={() => removeFromArrayField('better', index)}
                        className="ml-2 text-green-400 hover:text-green-600"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRemedyModal(false);
                    setEditingRemedy(null);
                  }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingRemedy ? handleEditRemedy : handleAddRemedy}
                  className="flex-1 py-3 bg-teal-500 text-white rounded-2xl font-medium"
                >
                  {editingRemedy ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar/Editar Curiosidade */}
      {showFactModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editingFact ? 'Editar Curiosidade' : 'Nova Curiosidade'}
                </h3>
                <button
                  onClick={() => {
                    setShowFactModal(false);
                    setEditingFact(null);
                  }}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                <input
                  type="text"
                  value={editingFact ? editingFact.title : newFact.title}
                  onChange={(e) => {
                    if (editingFact) {
                      setEditingFact({ ...editingFact, title: e.target.value });
                    } else {
                      setNewFact({ ...newFact, title: e.target.value });
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Digite o título da curiosidade"
                />
              </div>

              {/* Conteúdo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conteúdo</label>
                <textarea
                  value={editingFact ? editingFact.content : newFact.content}
                  onChange={(e) => {
                    if (editingFact) {
                      setEditingFact({ ...editingFact, content: e.target.value });
                    } else {
                      setNewFact({ ...newFact, content: e.target.value });
                    }
                  }}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                  placeholder="Digite o conteúdo da curiosidade"
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <select
                  value={editingFact ? editingFact.category : newFact.category}
                  onChange={(e) => {
                    const category = e.target.value as HomeopathyFact['category'];
                    if (editingFact) {
                      setEditingFact({ ...editingFact, category });
                    } else {
                      setNewFact({ ...newFact, category });
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="general">Geral</option>
                  <option value="history">História</option>
                  <option value="principles">Princípios</option>
                  <option value="remedies">Remédios</option>
                  <option value="research">Pesquisa</option>
                  <option value="other">Outros</option>
                </select>
              </div>

              {/* Status Ativo */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Curiosidade Ativa</label>
                <button
                  onClick={() => {
                    if (editingFact) {
                      setEditingFact({ ...editingFact, active: !editingFact.active });
                    } else {
                      setNewFact({ ...newFact, active: !newFact.active });
                    }
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    (editingFact ? editingFact.active : newFact.active) 
                      ? 'bg-yellow-500' 
                      : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    (editingFact ? editingFact.active : newFact.active) 
                      ? 'translate-x-6' 
                      : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>

            {/* Botões */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowFactModal(false);
                    setEditingFact(null);
                  }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingFact ? handleEditFact : handleAddFact}
                  className="flex-1 py-3 bg-yellow-500 text-white rounded-2xl font-medium"
                >
                  {editingFact ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seção SOS - Gerenciamento de Doenças */}
      <div ref={sosSectionRef} className="px-6 mb-6">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Sistema SOS - Doenças</h3>
                  <p className="text-sm text-gray-600">Gerenciar enfermidades e suas opções por grupos</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    resetDiseaseForm();
                    setDiseaseError(null);
                    setShowDiseaseModal(true);
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <i className="ri-add-line"></i>
                  Nova Doença
                </button>
                <button
                  onClick={() => void handleSeedSosDiseases()}
                  disabled={seedingSosDiseases}
                  className={`px-4 py-2 rounded-xl transition-colors flex items-center gap-2 ${
                    seedingSosDiseases
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-white text-red-500 border border-red-200 hover:bg-red-50'
                  }`}
                >
                  <i className="ri-upload-cloud-line"></i>
                  {seedingSosDiseases ? 'Importando...' : 'Importar doenças iniciais'}
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {loadingSosDiseases ? (
                <div className="text-center py-10 text-gray-500 flex flex-col items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-2xl"></i>
                  <span>Carregando doenças...</span>
                </div>
              ) : diseases.map((disease, index) => (
                <div key={disease.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                     onClick={() => {
                       setEditingDisease(disease);
                       setDiseaseError(null);
                       setShowDiseaseModal(true);
                     }}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-medicine-bottle-line text-red-500"></i>
                        <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                        <h4 className="font-semibold text-gray-800 text-lg">{disease.name}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{disease.description}</p>
                      
                      {/* Resumo dos grupos */}
                      <div className="flex flex-wrap gap-2">
                        {sosGroups.map((groupDef) => {
                          const groupOptions = disease.groups[groupDef.id] || [];
                          if (groupOptions.length === 0) return null;
                          
                          return (
                            <div key={groupDef.id} className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs">
                              <i className="ri-list-unordered text-xs"></i>
                              <span className="font-medium">{groupDef.title}</span>
                              <span className="bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
                                {groupOptions.length}
                              </span>
                            </div>
                          );
                        })}
                        {sosGroups.every(groupDef => (disease.groups[groupDef.id] || []).length === 0) && (
                          <span className="text-xs text-gray-400 italic">Nenhum grupo configurado</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDiseaseError(null);
                          setEditingDisease(disease);
                          setShowDiseaseModal(true);
                        }}
                        className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Editar doença"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeleteDisease(disease.id);
                        }}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Excluir doença"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {diseases.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <i className="ri-heart-pulse-line text-4xl mb-2 block"></i>
                  <p>Nenhuma doença cadastrada</p>
                  <p className="text-sm">Clique em "Nova Doença" para começar</p>
                </div>
              )}
            </div>

            {diseaseError && (
              <div className="mt-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm flex items-start gap-2">
                <i className="ri-error-warning-line text-lg"></i>
                <span>{diseaseError}</span>
              </div>
            )}
            {diseaseInfo && (
              <div className="mt-4 p-3 rounded-xl bg-blue-50 text-blue-600 text-sm flex items-start gap-2">
                <i className="ri-information-line text-lg"></i>
                <span>{diseaseInfo}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Adicionar/Editar Doença */}
      {showDiseaseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editingDisease ? 'Editar Doença' : 'Nova Doença'}
                </h3>
                <button
                  onClick={() => {
                    setShowDiseaseModal(false);
                    setEditingDisease(null);
                    resetDiseaseForm();
                  }}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
                {/* Nome da Doença */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Doença *</label>
                  <input
                    type="text"
                    value={editingDisease ? editingDisease.name : newDisease.name}
                    onChange={(e) => {
                      if (editingDisease) {
                        setEditingDisease({ ...editingDisease, name: e.target.value });
                      } else {
                        setNewDisease({ ...newDisease, name: e.target.value });
                      }
                    }}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-red-300 focus:outline-none"
                    placeholder="Ex: Febre, Dor de Cabeça"
                  />
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <textarea
                  value={editingDisease ? editingDisease.description : newDisease.description}
                  onChange={(e) => {
                    if (editingDisease) {
                      setEditingDisease({ ...editingDisease, description: e.target.value });
                    } else {
                      setNewDisease({ ...newDisease, description: e.target.value });
                    }
                  }}
                  className="w-full h-20 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:border-red-300 focus:outline-none"
                  placeholder="Descrição detalhada da doença"
                  maxLength={500}
                />
              </div>

              {/* Base de Conhecimento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base de Conhecimento (Arquivo .txt) *
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (editingDisease) {
                          setEditingDisease({ ...editingDisease, knowledgeBaseFile: file });
                        } else {
                          setNewDisease({ ...newDisease, knowledgeBaseFile: file });
                        }
                      }
                    }}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-red-300 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                  />
                  {(editingDisease?.knowledge_base_file || newDisease.knowledge_base_file) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-green-50 p-2 rounded-lg">
                      <i className="ri-file-text-line text-green-600"></i>
                      <span>
                        {editingDisease?.knowledgeBaseFile?.name || 
                         newDisease.knowledgeBaseFile?.name || 
                         editingDisease?.knowledge_base_file || 
                         'Arquivo carregado'}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Arquivo contendo os remédios e sintomas para esta doença. 
                    Será enviado para o bucket sos-knowledge.
                  </p>
                </div>
              </div>

              {/* Grupos e Opções */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Grupos e Opções</h4>
                <div className="space-y-4">
                  {sosGroups.map(group => {
                    const currentOptions = editingDisease 
                      ? editingDisease.groups[group.id] || []
                      : newDisease.groups[group.id] || [];
                    
                    const currentLimit = editingDisease
                      ? editingDisease.selectionLimits?.[group.id] ?? 1
                      : newDisease.selectionLimits?.[group.id] ?? 1;
                    
                    return (
                      <div key={group.id} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h5 className="font-medium text-gray-800">{group.title}</h5>
                            <span className="text-xs text-gray-500">({currentOptions.length} opções)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600">Máx. seleções:</label>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const newLimit = Math.max(1, currentLimit - 1);
                                  if (editingDisease) {
                                    setEditingDisease({
                                      ...editingDisease,
                                      selectionLimits: {
                                        ...editingDisease.selectionLimits,
                                        [group.id]: newLimit
                                      }
                                    });
                                  } else {
                                    setNewDisease({
                                      ...newDisease,
                                      selectionLimits: {
                                        ...newDisease.selectionLimits,
                                        [group.id]: newLimit
                                      }
                                    });
                                  }
                                }}
                                disabled={currentLimit <= 1}
                                className={`p-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors ${currentLimit <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title="Diminuir"
                              >
                                <i className="ri-arrow-down-s-line"></i>
                              </button>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={currentLimit}
                                onChange={(e) => {
                                  const newLimit = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                                  if (editingDisease) {
                                    setEditingDisease({
                                      ...editingDisease,
                                      selectionLimits: {
                                        ...editingDisease.selectionLimits,
                                        [group.id]: newLimit
                                      }
                                    });
                                  } else {
                                    setNewDisease({
                                      ...newDisease,
                                      selectionLimits: {
                                        ...newDisease.selectionLimits,
                                        [group.id]: newLimit
                                      }
                                    });
                                  }
                                }}
                                className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:border-red-300 focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newLimit = Math.min(10, currentLimit + 1);
                                  if (editingDisease) {
                                    setEditingDisease({
                                      ...editingDisease,
                                      selectionLimits: {
                                        ...editingDisease.selectionLimits,
                                        [group.id]: newLimit
                                      }
                                    });
                                  } else {
                                    setNewDisease({
                                      ...newDisease,
                                      selectionLimits: {
                                        ...newDisease.selectionLimits,
                                        [group.id]: newLimit
                                      }
                                    });
                                  }
                                }}
                                disabled={currentLimit >= 10}
                                className={`p-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors ${currentLimit >= 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title="Aumentar"
                              >
                                <i className="ri-arrow-up-s-line"></i>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Adicionar nova opção */}
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            value={tempInput[`group_${group.id}`] || ''}
                            onChange={e => setTempInput(prev => ({ ...prev, [`group_${group.id}`]: e.target.value }))}
                            placeholder={`Nova opção para ${group.title.toLowerCase()}`}
                            className="flex-1 p-2 border border-gray-200 rounded-lg text-sm focus:border-red-300 focus:outline-none"
                            onKeyDown={e => e.key === 'Enter' && addOptionToGroup(group.id)}
                          />
                          <button
                            type="button"
                            onClick={() => addOptionToGroup(group.id)}
                            className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>

                        {/* Lista de opções existentes */}
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragEnd={(event: DragEndEvent) => handleGroupOptionReorder(group.id, event)}
                        >
                          <SortableContext
                            items={currentOptions.map(getOptionIdentifier)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="flex flex-col gap-2">
                              {currentOptions.length === 0 ? (
                                <p className="text-xs text-gray-400">Nenhuma opção cadastrada.</p>
                              ) : (
                                currentOptions.map((option, index) => (
                                  <SortableGroupOption
                                    key={getOptionIdentifier(option)}
                                    option={option}
                                    onRemove={() => removeOptionFromGroup(group.id, index)}
                                  />
                                ))
                              )}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDiseaseModal(false);
                    setEditingDisease(null);
                    resetDiseaseForm();
                  }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (editingDisease) {
                      void handleEditDisease();
                    } else {
                      void handleAddDisease();
                    }
                  }}
                  disabled={savingDisease}
                  className={`flex-1 py-3 rounded-2xl font-medium ${savingDisease ? 'bg-red-300 text-white cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600'}`}
                >
                  {savingDisease ? 'Salvando...' : editingDisease ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Opção ao Grupo */}
      {showGroupOptionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">Nova Opção</h3>
                <button
                  onClick={() => {
                    setShowGroupOptionModal(false);
                    setSelectedDiseaseId('');
                    setSelectedGroupId('');
                    resetGroupOptionForm();
                  }}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Informações do Grupo */}
              <div className="bg-blue-50 rounded-2xl p-3">
                <p className="text-sm text-blue-700 font-medium">
                  Adicionando opção para: {sosGroups.find(g => g.id === selectedGroupId)?.title}
                </p>
                <p className="text-xs text-blue-600">
                  Doença: {diseases.find(d => d.id === selectedDiseaseId)?.name}
                </p>
              </div>

              {/* Label da Opção */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Opção</label>
                <input
                  type="text"
                  value={newGroupOption.label}
                  onChange={(e) => setNewGroupOption({ ...newGroupOption, label: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Ex: Dor latejante, Febre alta"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição (opcional)</label>
                <textarea
                  value={newGroupOption.description}
                  onChange={(e) => setNewGroupOption({ ...newGroupOption, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  placeholder="Descrição adicional da opção"
                />
              </div>
            </div>

            {/* Botões */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowGroupOptionModal(false);
                    setSelectedDiseaseId('');
                    setSelectedGroupId('');
                    resetGroupOptionForm();
                  }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddGroupOption}
                  className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-medium"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Badges / Conquistas */}
      <div ref={badgesSectionRef} className="px-6 mb-6">
        <BadgeManager
          badges={badges}
          onBadgeCreated={(badge) => setBadges([...badges, badge])}
          onBadgeUpdated={(badge) => setBadges(badges.map(b => b.id === badge.id ? badge : b))}
          onBadgeDeleted={(id) => setBadges(badges.filter(b => b.id !== id))}
        />
      </div>

      {/* Quiz Admin Panel */}
      {showQuizPanel && (
        <QuizAdminPanel onClose={() => setShowQuizPanel(false)} />
      )}
    </div>
  );
}





